import { env } from '@/env';
import {
  ApiClientError,
  type ApiError,
  ApiFailureSchema,
  type EndpointDef,
} from '@uchat-team-management/shared';
import { getAuth } from 'firebase/auth';
import type { ZodTypeAny, z } from 'zod';

export type Unwrap<R> = R extends { data: infer D } ? D : never;
export type MetaOf<R> = R extends { meta: infer M } ? M : undefined;

export interface CallResult<R> {
  data: Unwrap<R>;
  meta: MetaOf<R>;
}

interface CallInput<D extends EndpointDef<ZodTypeAny, ZodTypeAny, ZodTypeAny, ZodTypeAny>> {
  params?: z.input<D['params']>;
  query?: z.input<D['query']>;
  body?: z.input<D['body']>;
  signal?: AbortSignal;
}

type NavigateFn = (path: string) => void;

let onUnauthenticated: (() => Promise<void> | void) | null = null;
let onEmailNotAuthorized: ((email: string | undefined) => void) | null = null;
let navigate: NavigateFn | null = null;

export const configureApiClient = (opts: {
  onUnauthenticated?: () => Promise<void> | void;
  onEmailNotAuthorized?: (email: string | undefined) => void;
  navigate?: NavigateFn;
}): void => {
  if (opts.onUnauthenticated) onUnauthenticated = opts.onUnauthenticated;
  if (opts.onEmailNotAuthorized) onEmailNotAuthorized = opts.onEmailNotAuthorized;
  if (opts.navigate) navigate = opts.navigate;
};

const interpolatePath = (path: string, params: Record<string, unknown> | undefined): string => {
  if (!params) return path;
  return path.replace(/:(\w+)/g, (_, key: string) => {
    const value = params[key];
    if (value === undefined) {
      throw new Error(`Missing path parameter "${key}" for ${path}`);
    }
    return encodeURIComponent(String(value));
  });
};

const buildQuery = (query: Record<string, unknown> | undefined): string => {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === undefined || item === null) continue;
        params.append(key, String(item));
      }
    } else {
      params.append(key, String(value));
    }
  }
  const s = params.toString();
  return s ? `?${s}` : '';
};

const readIdToken = async (): Promise<string | null> => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken();
};

const handleErrorResponse = async (status: number, json: unknown): Promise<never> => {
  const parsed = ApiFailureSchema.safeParse(json);
  const error: ApiError = parsed.success
    ? parsed.data.error
    : {
        code: 'INTERNAL_ERROR',
        message: 'Malformed error envelope from server',
        requestId: 'unknown',
      };

  if (status === 401) {
    if (onUnauthenticated) await onUnauthenticated();
    if (navigate) navigate('/login?reason=expired');
  } else if (
    status === 403 &&
    (error.code === 'EMAIL_NOT_AUTHORIZED' || error.code === 'ACCOUNT_DISABLED')
  ) {
    const email = typeof error.details?.email === 'string' ? error.details.email : undefined;
    if (onEmailNotAuthorized) onEmailNotAuthorized(email);
    if (navigate)
      navigate(
        error.code === 'ACCOUNT_DISABLED' ? '/authorize-error?reason=disabled' : '/authorize-error',
      );
  }

  throw new ApiClientError(status, error);
};

export const apiCall = async <
  D extends EndpointDef<ZodTypeAny, ZodTypeAny, ZodTypeAny, ZodTypeAny>,
>(
  def: D,
  input: CallInput<D> = {},
): Promise<CallResult<z.infer<D['response']>>> => {
  const path = interpolatePath(def.path, input.params as Record<string, unknown> | undefined);
  const queryString = buildQuery(input.query as Record<string, unknown> | undefined);
  const url = `${env.VITE_API_BASE_URL.replace(/\/$/, '')}${path}${queryString}`;

  const headers: Record<string, string> = {
    Accept: 'application/json',
  };

  const token = await readIdToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  let bodyText: string | undefined;
  if (input.body !== undefined && def.method !== 'GET') {
    bodyText = JSON.stringify(input.body);
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(url, {
    method: def.method,
    headers,
    body: bodyText,
    signal: input.signal,
  });

  if (res.status === 204) {
    return {
      data: undefined as Unwrap<z.infer<D['response']>>,
      meta: undefined as MetaOf<z.infer<D['response']>>,
    };
  }

  const json = await res.json().catch(() => ({}));

  if (!res.ok) {
    await handleErrorResponse(res.status, json);
  }

  if (import.meta.env.DEV) {
    const parsed = def.response.safeParse(json);
    if (!parsed.success) {
      console.error('[api] Response shape mismatch', def.path, parsed.error.issues, json);
    }
  }

  const data = (json as { data?: unknown }).data;
  const meta = (json as { meta?: unknown }).meta;
  return {
    data: data as Unwrap<z.infer<D['response']>>,
    meta: meta as MetaOf<z.infer<D['response']>>,
  };
};

/**
 * Opens a text/event-stream endpoint with the same Bearer auth as apiCall
 * (native EventSource can't send headers). Returns the raw Response; the caller
 * reads `body` and handles reconnects.
 */
export const apiStream = async <
  D extends EndpointDef<ZodTypeAny, ZodTypeAny, ZodTypeAny, ZodTypeAny>,
>(
  def: D,
  input: Pick<CallInput<D>, 'params' | 'signal'>,
): Promise<Response> => {
  const path = interpolatePath(def.path, input.params as Record<string, unknown> | undefined);
  const headers: Record<string, string> = { Accept: 'text/event-stream' };
  const token = await readIdToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(`${env.VITE_API_BASE_URL.replace(/\/$/, '')}${path}`, {
    headers,
    signal: input.signal,
  });
};

export { ApiClientError };
